import React, { createContext, useContext, useState } from 'react';
import { CloudData } from '../../../data/cloud-data.js';
import { Configuration, LeatherTypes, SeatBackStyles } from '../../../data/configuration.js';
import { InteriorConfigurator } from '../../../configurator/interior-configurator.js';
import { VehicleView } from '../../vehicle-view.js';
import { SectionPanel, SectionItem, ItemProps } from '../../view-content.js';
import SelectedSectionItem from '../../ui/selected-section-item.tsx';
import ViewContentRows from '../../ui/view-content-rows.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface LeatherViewProps {
	interiorConfigurator: InteriorConfigurator;
	onBackButtonClicked: () => void;
}

interface LeatherTypeViewProps {
	leatherType: LeatherTypes;
	isClosed?: boolean;
	onPanelOpenClose?: (isOpen: boolean) => void;
}

interface LeatherItemsProps {
	leatherType: LeatherTypes;
}

interface LeatherItemProps {
	id: string;
}

interface LeatherViewContextType {
	selectedLeatherItemId: string;
	selectedLeatherItemName: string;
	handleLeatherItemClicked: (id: string) => void;
	handleTunnelSideItemClicked: (id: string) => void;
	selectedTunnelSideId: string;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function LeatherView(props: LeatherViewProps) {

	// initial values.
	const initialLeatherItemId = Configuration.leatherStyle;
	const initialLeatherItemName = () => getItemName(initialLeatherItemId);
	const initialTunnelSideId = Configuration.tunnelSidesStyle;
	const initialTunnelSideName = () => getItemName(initialTunnelSideId);

	// the upholstery types are presented as an accordion of one panel per type, of which the panel holding the selected leather is the one open on arrival.
	const initialOpenLeatherType = () => CloudData.getLeatherTypesForId(initialLeatherItemId).leatherType;

	// states.
	const [selectedLeatherItemId, setSelectedLeatherItemId] = useState(initialLeatherItemId);
	const [selectedLeatherItemName, setSelectedLeatherItemName] = useState(initialLeatherItemName);
	const [selectedTunnelSideId, setSelectedTunnelSideId] = useState(initialTunnelSideId);
	const [selectedTunnelSideName, setSelectedTunnelSideName] = useState(initialTunnelSideName);

	// the one upholstery type panel currently open, or null when the customer has closed them all, as the paintwork and wheels views hold
	// their own accordions. there is no separate 'selected leather type' any more - the type is whichever one the selected leather belongs to.
	const [openLeatherType, setOpenLeatherType] = useState<LeatherTypes | null>(initialOpenLeatherType);

	// handlers.

	const handlePanelOpenClose = (leatherTypePanel: LeatherTypes, isOpen: boolean) => {

		// only one panel is open at a time, so opening one closes the others, and closing one leaves none open.
		setOpenLeatherType(isOpen ? leatherTypePanel : null);
	};

	const handleLeatherItemClicked = (id: string) => {

		// take the upholstery type from the item clicked rather than from a separately held "selected type", which the accordion no
		// longer needs - and which could lag the click in any case.
		const leatherType = CloudData.getLeatherTypesForId(id).leatherType;

		// update the selected item and item name.
		setSelectedLeatherItemId(id);
		setSelectedLeatherItemName(getItemName(id));

		// update the configuration.
		Configuration.leatherStyle = id;
		Configuration.updateCosts();

		// if the seat stitching is set to 'matching leather', update it to match the newly selected leather.
		// n.b. has to be done before selecting the leather style on the car due to how the "_setOrFadeSeatMaterial" function works in the InteriorConfigurator.
		if (Configuration.seatStitchingOption === Configuration.stitchingMatchingLeatherStyleId) {
			props.interiorConfigurator.selectSeatStitchingStyle(Configuration.seatStitchingOption);
		}

		// select the leather style on the car.
		props.interiorConfigurator.selectLeatherStyle(id);

		// if the selected leather type is two-tone...
		if (leatherType === LeatherTypes.TwoTone) {

			// set the appropriate seat back style (updating the configuration and selecting the style on the car).
			Configuration.seatBackStyle = SeatBackStyles.TwoToneTextile;
			Configuration.updateCosts();
			props.interiorConfigurator.selectSeatBackStyle(SeatBackStyles.TwoToneTextile);
			
			// set the appropriate seat pleating style (updating the configuration and selecting the style on the car).
			Configuration.seatPleatingOption = Configuration.seatPleatingOptionForTwoToneLeather;
			Configuration.updateCosts();
			props.interiorConfigurator.selectSeatPleatingStyle(Configuration.seatPleatingOption);
		}

		// otherwise, if two-tone was selected, set the seat back style to the default leather wrapped style.
		else if (Configuration.seatBackStyle === SeatBackStyles.TwoToneTextile) {

			// update the configuration and select the seat back style on the car.
			Configuration.seatBackStyle = SeatBackStyles.LeatherWrapped;
			Configuration.updateCosts();
			props.interiorConfigurator.selectSeatBackStyle(SeatBackStyles.LeatherWrapped);
		}
	};

	const handleTunnelSideItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedTunnelSideId(id);
		setSelectedTunnelSideName(getItemName(id));

		// update the configuration.
		Configuration.tunnelSidesStyle = id;
		Configuration.updateCosts();

		// select the tunnel sides style on the car.
		props.interiorConfigurator.selectTunnelSidesStyle();
	}

	// return the HTML.
	return (
		<LeatherViewContext.Provider value={{ selectedLeatherItemId, selectedLeatherItemName, handleLeatherItemClicked, handleTunnelSideItemClicked, selectedTunnelSideId }}>
			<VehicleView
				onNextButtonClicked={props.onBackButtonClicked}>
				<div id="view-content">
					<div id="view-content-header">
						<h2 id="ga4-interior-upholstery">
							INTERIOR UPHOLSTERY
						</h2>
					</div>

					{/* the upholstery types are an accordion of one panel each, holding that type's colours, as the paintwork and wheels steps are. */}
					<LeatherTypeView
						leatherType={LeatherTypes.TwoTone}
						isClosed={openLeatherType !== LeatherTypes.TwoTone}
						onPanelOpenClose={(isOpen) => handlePanelOpenClose(LeatherTypes.TwoTone, isOpen)}
					/>

					<LeatherTypeView
						leatherType={LeatherTypes.PebbleGrain}
						isClosed={openLeatherType !== LeatherTypes.PebbleGrain}
						onPanelOpenClose={(isOpen) => handlePanelOpenClose(LeatherTypes.PebbleGrain, isOpen)}
					/>

					<LeatherTypeView
						leatherType={LeatherTypes.SoftGrain}
						isClosed={openLeatherType !== LeatherTypes.SoftGrain}
						onPanelOpenClose={(isOpen) => handlePanelOpenClose(LeatherTypes.SoftGrain, isOpen)}
					/>

					<SectionPanel
						subHeading={'TUNNEL SIDES'}
						modalContent={CloudData.getModalContent(selectedTunnelSideId, CloudData.leatherStyleOptions)}
					>
						<SelectedSectionItem
							itemName={selectedTunnelSideName}
							sectionPrice={CloudData.getPrice(selectedTunnelSideId, CloudData.leatherStyleOptions, { ref: false })}
						/>
						<TunnelSideItems />
					</SectionPanel>

				</div>
			</VehicleView>
		</LeatherViewContext.Provider>
	);
}

//-----------------------------------------------------------------------------------------
// Public Functions:
//-----------------------------------------------------------------------------------------

// used by views that have a 'matched leather' option in order to display a swatch showing the currently selected leather colour in the chosen type.
// n.b. not to be confused with "getFilePathForLeatherItemId" below, which covers the same IDs but returns the leather option's own image. the two
// differ for every two-tone ID - LP018 is "pebble-grain/black" here (the grain it matches) but "two-tone/pebble-black-grey" there (its own swatch).
//
// !! the values below are not just this folder's filenames - they are the tail of a path built against several image trees, so a value
// here must exist as a file in every one of them:
//   assets/images/configuration-items/interior/leather/<value>.webp                     (seats-view)
//   assets/images/configuration-items/interior/seat-pleating/<style>/<value>.webp       (seats-view, one per seat pleating style)
// so re-coding or re-categorising a leather colour means renaming or adding the images in ALL of those trees, not only adding the row
// here. nothing checks this - a value with no matching file simply renders a broken image for that colour, and only on the step that
// uses that tree. this has already bitten us: the seat pleating icons for British Green Leaf, Cotswold Green, Light Grey, Aubergine and
// Fawn were missing because those colours were re-coded here and the seat-pleating artwork was left under the old names and grains.
export function getFilePathForLeatherStyleId(id: string): string {

	// map the leather style IDs to the file paths for the relevant swatches.
	const filePaths: Record<string, string> = {

		// pebble grain.
		LP001: 'pebble-grain/black',
		LP003: 'pebble-grain/tan',
		LP002: 'pebble-grain/dark-brown',
		LP013: 'pebble-grain/slate',
		LP010: 'pebble-grain/biscuit',
		LP011: 'pebble-grain/cream',
		LP006: 'pebble-grain/british-green',
		LP009: 'pebble-grain/british-blue',

		// soft grain.
		LS002: 'soft-grain/black',
		LS003: 'soft-grain/dark-brown',
		LS001: 'soft-grain/rich-tan',
		LS004: 'soft-grain/deep-red',
		LS005: 'soft-grain/ivory',
		LS010: 'soft-grain/british-green-leaf',
		LS008: 'soft-grain/aubergine',
		LS009: 'soft-grain/fawn',
		LP007: 'soft-grain/light-grey',
		LS011: 'soft-grain/cotswold-green',

		// two-tone - pebble grain.
		LP018: 'pebble-grain/black',
		LP019: 'pebble-grain/british-blue',
		LP020: 'pebble-grain/slate',

		// two-tone - soft grain.
		LP021: 'soft-grain/ivory',
		LP024: 'soft-grain/british-green-leaf',
		LP023: 'soft-grain/dark-brown'
	};

	// return the corresponding path.
	const filePath = filePaths[id];
	if (filePath) {
		return filePath;
	}

	// or return a fail-save default if not found.
	return 'pebble-grain/black';
}

//-----------------------------------------------------------------------------------------
// Private Components:
//-----------------------------------------------------------------------------------------

// context to allow any view in the hierarchy to access the selected item id and call the item clicked handler.
const LeatherViewContext = createContext<LeatherViewContextType | undefined>(undefined);

function LeatherTypeView(props: LeatherTypeViewProps) {

	// get the context and ensure it is valid.
	const context = useContext(LeatherViewContext);
	if (!context) {
		throw new Error('LeatherViewContext must be used within a LeatherView component');
	}

	// return the HTML.
	// n.b. the name, price and info shown are the selected leather's, repeated on every panel - they describe the configuration, so they
	// must not change as panels are opened and closed.
	return (
		<SectionPanel
			subHeading={getTypeName(props.leatherType).toUpperCase()}
			modalContent={CloudData.getModalContent(context.selectedLeatherItemId, CloudData.leatherStyleOptions)}
			isClosed={props.isClosed}
			onPanelOpenClose={props.onPanelOpenClose}
		>
			<SelectedSectionItem
				itemName={context.selectedLeatherItemName}
				sectionPrice={CloudData.getPrice(context.selectedLeatherItemId, CloudData.leatherStyleOptions, { ref: false })}
			/>
			<LeatherItems leatherType={props.leatherType} />
		</SectionPanel>
	);
}

function LeatherItems(props: LeatherItemsProps) {
	switch (props.leatherType) {
		case LeatherTypes.PebbleGrain:
			return <LeatherItemsPebbleGrain />;
		case LeatherTypes.SoftGrain:
			return <LeatherItemsSoftGrain />;
		default:
			return <LeatherItemsTwoTone />;
	}
};

function LeatherItemsTwoTone() {
	return (
		<ViewContentRows>
			<LeatherItem id={'LP018'} />
			<LeatherItem id={'LP019'} />
			<LeatherItem id={'LP020'} />
			<LeatherItem id={'LP021'} />
			<LeatherItem id={'LP024'} />
			<LeatherItem id={'LP023'} />
		</ViewContentRows>
	);
}

function LeatherItemsPebbleGrain() {
	return (
		<ViewContentRows maxRowLength={6}>
			<LeatherItem id={'LP001'} />
			<LeatherItem id={'LP003'} />
			<LeatherItem id={'LP002'} />
			<LeatherItem id={'LP013'} />
			<LeatherItem id={'LP010'} />
			<LeatherItem id={'LP011'} />
			<LeatherItem id={'LP006'} />
			<LeatherItem id={'LP009'} />
		</ViewContentRows>
	);
}

function LeatherItemsSoftGrain() {
	return (
		<ViewContentRows maxRowLength={4}>
			<LeatherItem id={'LS002'} />
			<LeatherItem id={'LS003'} />
			<LeatherItem id={'LS001'} />
			<LeatherItem id={'LS004'} />
			<LeatherItem id={'LS005'} />
			<LeatherItem id={'LS010'} />
			<LeatherItem id={'LS008'} />
			<LeatherItem id={'LS009'} />
			<LeatherItem id={'LP007'} />
			<LeatherItem id={'LS011'} />
		</ViewContentRows>
	);
}

function LeatherItem(props: LeatherItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(LeatherViewContext);
	if (!context) {
		throw new Error('LeatherViewContext must be used within a LeatherView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/interior/leather/${getFilePathForLeatherItemId(props.id)}.webp`}
			isSelected={props.id === context.selectedLeatherItemId}
			onItemClicked={context.handleLeatherItemClicked}
		/>
	);
}

function TunnelSideItems() {

	// get the context and ensure it is valid.
	const context = useContext(LeatherViewContext);
	if (!context) {
		throw new Error('LeatherViewContext must be used within a LeatherView component');
	}

	// get the correct image path to show the selected carpet.
	const handleCarpetImagePath = () => {
		const carpetName = CloudData.getName(Configuration.carpetColour, CloudData.carpetColourOptions);

		// get carpet colour to create the image path.
		const carpetNameSplit = carpetName.split(' ');
		const carpetColour = carpetNameSplit[carpetNameSplit.length - 1];

		return `${carpetColour.toLowerCase()}-box-weave`;
	}

	// the leather tunnel sides show the leather currently selected, so the swatch is derived from that selection.
	const leatherImageFilePath = `assets/images/configuration-items/interior/leather/${getFilePathForLeatherItemId(context.selectedLeatherItemId)}.webp`;

	return (
		<ViewContentRows>
			<TunnelSideItem id="LO001" imageFilePath={`assets/images/configuration-items/interior/carpet/${handleCarpetImagePath()}.webp`} />
			<TunnelSideItem id="LO002" imageFilePath={leatherImageFilePath} />
		</ViewContentRows>
	);
}

function TunnelSideItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(LeatherViewContext);
	if (!context) {
		throw new Error('LeatherViewContext must be used within a LeatherView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={props.imageFilePath}
			isSelected={props.id === context.selectedTunnelSideId}
			onItemClicked={context.handleTunnelSideItemClicked}
		/>
	);
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// returns the file path for a leather option's own image - the swatch shown for it in the upholstery colour rows, and on the leather tunnel sides
// option. it is the only place these are named, as the item rows look their own image up from their ID.
// n.b. not to be confused with "getFilePathForLeatherStyleId" above; see the note there for how the two differ.
function getFilePathForLeatherItemId(id: string): string {

	// map the leather style IDs to the file paths for their own swatches.
	const filePaths: Record<string, string> = {

		// pebble grain.
		LP001: 'pebble-grain/black',
		LP003: 'pebble-grain/tan',
		LP002: 'pebble-grain/dark-brown',
		LP013: 'pebble-grain/slate',
		LP010: 'pebble-grain/biscuit',
		LP006: 'pebble-grain/british-green',
		LP009: 'pebble-grain/british-blue',
		LP011: 'pebble-grain/cream',

		// soft grain.
		LS002: 'soft-grain/black',
		LS003: 'soft-grain/dark-brown',
		LS001: 'soft-grain/rich-tan',
		LS004: 'soft-grain/deep-red',
		LS005: 'soft-grain/ivory',
		LS010: 'soft-grain/british-green-leaf',
		LS008: 'soft-grain/aubergine',
		LS009: 'soft-grain/fawn',
		LP007: 'soft-grain/light-grey',
		LS011: 'soft-grain/cotswold-green',

		// two-tone.
		LP018: 'two-tone/pebble-black-grey',
		LP019: 'two-tone/pebble-british-blue',
		LP020: 'two-tone/pebble-grey',
		LP021: 'two-tone/soft-ivory',
		LP024: 'two-tone/soft-british-green-leaf',
		LP023: 'two-tone/soft-dark-brown'
	};

	// return the corresponding path.
	const filePath = filePaths[id];
	if (filePath) {
		return filePath;
	}

	// or return a default if the ID is not one we hold an image for.
	return 'pebble-grain/black';
}

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getItemName(id: string): string {
	return CloudData.getName(id, CloudData.leatherStyleOptions);
}

// returns the type name from the given type, used to head that type's accordion panel.
// n.b. we can't just use the string value of the enum as "two-tone" needs to be hyphenated.
function getTypeName(leatherType: LeatherTypes): string {
	switch (leatherType) {
		case LeatherTypes.PebbleGrain:
			return 'Pebble Grain';
		case LeatherTypes.SoftGrain:
			return 'Soft Grain';
		default:
			return 'Two-Tone';
	}
}
